// Catalogue Manager — CSV upload + category management for the retailer's
// private product catalogue (powers Quote Builder autocomplete).
//
// Source-of-truth: /var/www/kitchenos/CatalogueManager.js (212 lines, full file)
//   Page wiring  : /var/www/kitchenos/portal-standalone.html
//                    line 1990–1991 (page-section markup)
//                    line 4715–4718 (init hook — CatalogueManager.init({ type:'retailer', id:_rid }))
//
// Backend: /var/www/kitchenos-api/modules/catalogue.js (mounted at /api/catalogue/*)
//   GET  /api/catalogue/categories?type=retailer&id=<rid> → { categories: [{ name, productCount }] }
//   POST /api/catalogue/categories  body { type, id, categories }     → { ok: true }
//   POST /api/catalogue/upload      multipart (Busboy): fields type, id, name,
//                                     category, mapping (JSON) + csv file part
//                                     → { ok, count } | { ok:false, error }
//
// This module ports CatalogueManager.js verbatim for markup, class names,
// inline styles, copy, mapping fields, naive CSV parsing, and guessMapping
// table. Source's `onclick="CatalogueManager.foo()"` global-call pattern is
// replaced with delegated `addEventListener` since the modular page forbids
// globals on window — DOM the user sees is identical.
//
// Departure from source: the upload uses FormData (multipart) instead of
// JSON. The live backend uses Busboy, which expects multipart. Source's
// JSON body would 400 with "missing fields"; FormData is the working
// protocol — confirmed against /var/www/kitchenos-api/modules/catalogue.js.

import { alertDialog, iconMarkup, promptDialog } from "@tkos/shared-ui";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

// ── Types ────────────────────────────────────────────────────────────────────

type MappingField =
  | "sku"
  | "description"
  | "tradePrice"
  | "rrp"
  | "h"
  | "w"
  | "d"
  | "doorRange"
  | "colour";

type Mapping = Record<MappingField, string>;

interface State {
  type: string;
  id: string;
  categories: string[];
  currentCategory: string | null;
  csvHeaders: string[];
  csvRows: string[][];
  mapping: Mapping;
}

interface CategoriesResponse {
  // Backend returns objects with productCount; this page only needs names,
  // so we extract `name` at the boundary. See loadCategories below.
  categories?: Array<{ name?: string; productCount?: number }>;
}

interface UploadResponse {
  ok: boolean;
  count?: number;
  error?: string;
}

// ── HTML escape (markup carries cat names, headers, etc) ─────────────────────

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Mapping guess (verbatim from source) ─────────────────────────────────────

const GUESS_MAP: Record<MappingField, string[]> = {
  sku: ["sku", "code", "product code", "part number"],
  description: ["description", "desc", "name", "unit_desc"],
  tradePrice: ["trade", "cost", "buy", "price"],
  rrp: ["rrp", "list", "retail", "selling"],
  h: ["h", "height"],
  w: ["w", "width"],
  d: ["d", "depth"],
  doorRange: ["door", "range", "style"],
  colour: ["colour", "color", "finish"],
};

function guessMapping(field: MappingField, header: string): boolean {
  const h = header.toLowerCase();
  const patterns = GUESS_MAP[field];
  return patterns.some((p) => h.indexOf(p) !== -1);
}

// ── Mapping field spec (verbatim labels from source) ─────────────────────────

interface FieldSpec {
  id: MappingField;
  label: string;
}

const FIELDS: FieldSpec[] = [
  { id: "sku", label: "Product Code / SKU" },
  { id: "description", label: "Description" },
  { id: "tradePrice", label: "Trade Price" },
  { id: "rrp", label: "RRP / List Price" },
  { id: "h", label: "Height (mm)" },
  { id: "w", label: "Width (mm)" },
  { id: "d", label: "Depth (mm)" },
  { id: "doorRange", label: "Door Range / Style" },
  { id: "colour", label: "Colour / Finish" },
];

// ── Factory: every mount() gets its own isolated state ───────────────────────

function makeInitialState(type: string, id: string): State {
  return {
    type,
    id,
    categories: [],
    currentCategory: null,
    csvHeaders: [],
    csvRows: [],
    mapping: {
      sku: "",
      description: "",
      tradePrice: "",
      rrp: "",
      h: "",
      w: "",
      d: "",
      doorRange: "",
      colour: "",
    },
  };
}

// ── Page ─────────────────────────────────────────────────────────────────────

const catalogueManagerPage: Page = {
  name: "catalogue-manager",
  label: "Catalogue Manager",
  icon: "",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    // Source: `_rid` in portal-standalone.html (line 4717). In modular, the
    // retailer's id is exposed on ctx.user.retailerId (see suppliers/page.ts
    // line ~696). Fallback to empty string keeps render safe if anonymous.
    const retailerId = ctx.user?.retailerId ?? "";

    const state: State = makeInitialState("retailer", retailerId);

    // The source mounts into `#catalogue-manager-root`; in modular each page
    // owns the chrome-provided `container`. Source padding `style="padding:20px"`
    // came from the host page-section — we apply it on our own root wrapper.
    container.innerHTML =
      '<div id="catalogue-manager-root" style="padding: 20px;"></div>';
    const root = container.querySelector<HTMLElement>(
      "#catalogue-manager-root",
    )!;

    let disposed = false;

    // ── Render functions (mirror source render()/renderUploadUI()/renderMappingUI()) ──

    function render(): void {
      if (disposed) return;

      const catListHtml = state.categories
        .map((cat) => {
          const isActive = state.currentCategory === cat;
          const activeClass = isActive ? "active" : "";
          const activeStyle = isActive
            ? "background: var(--brand-2); color: var(--text); font-weight: 600;"
            : "";
          return (
            '<div class="cat-item ' +
            activeClass +
            '" data-action="select-category" data-cat="' +
            escapeHtml(cat) +
            '" style="padding: 10px; border-radius: 8px; cursor: pointer; margin-bottom: 4px; ' +
            activeStyle +
            '">' +
            escapeHtml(cat) +
            "</div>"
          );
        })
        .join("");

      const rightPaneHtml = state.currentCategory
        ? renderUploadUI()
        : '<div style="color: var(--text-dim); padding: 40px; text-align: center;">Select a category to manage products</div>';

      root.innerHTML =
        '<div class="card">' +
        '<div class="card-h">' +
        "<div>" +
        "<h3>Catalogue Management</h3>" +
        '<div class="sub">Upload CSVs to populate your product list</div>' +
        "</div>" +
        '<button class="btn primary sm" data-action="add-category">+ Add Category</button>' +
        "</div>" +
        '<div style="display:grid; grid-template-columns: 240px 1fr; gap: 20px;">' +
        '<div style="border-right: 1px solid var(--border); padding-right: 20px;">' +
        catListHtml +
        "</div>" +
        "<div>" +
        rightPaneHtml +
        "</div>" +
        "</div>" +
        "</div>";

      // If the right pane is the upload UI and we already have csvHeaders
      // loaded (e.g. user re-rendered after category-add), repaint the
      // mapping UI too. Source doesn't do this (it re-renders the whole
      // page and loses the mapping); we mirror source exactly — mapping is
      // cleared on each render. No re-paint here.
    }

    function renderUploadUI(): string {
      const cat = state.currentCategory ?? "";
      const safeCat = escapeHtml(cat);
      return (
        "<h4>Managing: " +
        safeCat +
        "</h4>" +
        '<div style="margin: 20px 0; padding: 30px; border: 2px dashed var(--border); border-radius: 12px; text-align: center;">' +
        '<input type="file" id="csv-upload" accept=".csv" style="display: none;" data-action="file-input">' +
        '<button class="btn primary" data-action="trigger-upload">' +
        '<span class="ic">' + iconMarkup("upload", { size: "sm" }) + '</span>' +
        " Upload CSV for " +
        safeCat +
        "</button>" +
        '<div style="margin-top: 10px; font-size: 12px; color: var(--text-dim);">Updates existing prices or adds new products</div>' +
        "</div>" +
        '<div id="mapping-ui"></div>'
      );
    }

    function renderMappingUI(): void {
      const mappingHost = root.querySelector<HTMLElement>("#mapping-ui");
      if (!mappingHost) return;

      const fieldHtml = FIELDS.map((f) => {
        const optionsHtml = state.csvHeaders
          .map((h) => {
            const selected = guessMapping(f.id, h) ? "selected" : "";
            const safeH = escapeHtml(h);
            return (
              '<option value="' +
              safeH +
              '" ' +
              selected +
              ">" +
              safeH +
              "</option>"
            );
          })
          .join("");

        return (
          '<div style="display: flex; flex-direction: column; gap: 4px;">' +
          '<label style="font-size: 11px; font-weight: 600; color: var(--text-dim);">' +
          escapeHtml(f.label) +
          "</label>" +
          '<select data-action="update-mapping" data-field="' +
          f.id +
          '" style="padding: 8px; border-radius: 6px; border: 1px solid var(--border); background: var(--surface-2); color: var(--text);">' +
          '<option value="">-- Select Column --</option>' +
          optionsHtml +
          "</select>" +
          "</div>"
        );
      }).join("");

      mappingHost.innerHTML =
        '<div style="margin-top: 20px; border-top: 1px solid var(--border); padding-top: 20px;">' +
        "<h5>Map CSV Columns</h5>" +
        '<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px;">' +
        fieldHtml +
        "</div>" +
        '<button class="btn primary lg" style="width: 100%;" data-action="confirm-upload">Confirm &amp; Import Products</button>' +
        "</div>";

      // Auto-select guessed mapping (matches source: write to state.mapping
      // so confirmUpload picks it up without a manual change event).
      for (const f of FIELDS) {
        const guessed = state.csvHeaders.find((h) => guessMapping(f.id, h));
        if (guessed) state.mapping[f.id] = guessed;
      }
    }

    // ── Actions (mirror source's exposed methods) ─────────────────────────

    async function loadCategories(): Promise<void> {
      try {
        const data = await ctx.api<CategoriesResponse>(
          "/catalogue/categories?type=" +
            encodeURIComponent(state.type) +
            "&id=" +
            encodeURIComponent(state.id),
        );
        if (disposed) return;
        state.categories = (data.categories ?? [])
          .map((c) => String(c?.name ?? ""))
          .filter((n) => n.length > 0);
        render();
      } catch {
        if (disposed) return;
        // Source ignores load errors silently; we surface a toast but keep
        // categories empty so the page still renders.
        state.categories = [];
        render();
        ctx.showToast("Could not load catalogue categories", "error");
      }
    }

    async function addCategory(): Promise<void> {
      const name = await promptDialog(
        "Enter category name (e.g. Base Units, Wall Units, Appliances):",
        { title: "Add category", confirmLabel: "Add" },
      );
      if (name && state.categories.indexOf(name) === -1) {
        state.categories.push(name);
        try {
          await ctx.api<unknown>("/catalogue/categories", {
            method: "POST",
            body: JSON.stringify({
              type: state.type,
              id: state.id,
              categories: state.categories,
            }),
          });
        } catch {
          // Source ignores POST errors (it always re-renders). Match: re-render
          // and toast so the user knows the save failed.
          ctx.showToast("Could not save category", "error");
        }
        if (!disposed) render();
      }
    }

    function selectCategory(cat: string): void {
      state.currentCategory = cat;
      render();
    }

    function handleFileUpload(event: Event): void {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>): void => {
        const result = e.target?.result;
        if (typeof result !== "string") return;

        const lines = result.split(/\r?\n/);
        if (lines.length < 2) {
          void alertDialog("CSV is empty or invalid", { title: "CSV invalid" });
          return;
        }

        // Source uses naive `.split(',')` — preserved verbatim per
        // extraction instructions, even though it breaks on quoted commas.
        state.csvHeaders = (lines[0] ?? "")
          .split(",")
          .map((h) => h.trim().replace(/^"|"$/g, ""));
        state.csvRows = lines
          .slice(1)
          .map((l) =>
            l.split(",").map((v) => v.trim().replace(/^"|"$/g, "")),
          );

        renderMappingUI();
      };
      reader.readAsText(file);
    }

    function updateMapping(field: string, value: string): void {
      if (isMappingField(field)) {
        state.mapping[field] = value;
      }
    }

    async function confirmUpload(): Promise<void> {
      const csvContent =
        state.csvHeaders.join(",") +
        "\n" +
        state.csvRows.map((r) => r.join(",")).join("\n");

      // Backend uses Busboy multipart parsing — needs FormData with the CSV
      // as a file part, not a JSON body. Bypass ctx.api (which forces
      // application/json) and let the browser set the multipart boundary.
      const fd = new FormData();
      fd.append("type", state.type);
      fd.append("id", state.id);
      fd.append("name", state.id);
      fd.append("category", state.currentCategory ?? "");
      fd.append("mapping", JSON.stringify(state.mapping));
      fd.append(
        "csv",
        new Blob([csvContent], { type: "text/csv" }),
        "upload.csv",
      );

      try {
        const r = await fetch("/api/catalogue/upload", {
          method: "POST",
          credentials: "same-origin",
          body: fd,
        });
        if (disposed) return;

        let data: UploadResponse;
        try {
          data = (await r.json()) as UploadResponse;
        } catch {
          data = { ok: false, error: `HTTP ${r.status}` };
        }

        if (data.ok) {
          await alertDialog(
            "Successfully imported " + String(data.count ?? 0) + " products!",
            { title: "Import complete" },
          );
          state.csvHeaders = [];
          state.csvRows = [];
          render();
        } else {
          await alertDialog("Upload failed: " + String(data.error ?? "unknown"), {
            title: "Upload failed",
          });
        }
      } catch (err) {
        if (disposed) return;
        const msg = err instanceof Error ? err.message : "unknown";
        await alertDialog("Upload failed: " + msg, { title: "Upload failed" });
      }
    }

    // ── Event delegation (replaces source's onclick="CatalogueManager.X()") ──

    function onClick(ev: Event): void {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      const actionEl = target.closest<HTMLElement>("[data-action]");
      if (!actionEl) return;
      const action = actionEl.dataset["action"];
      switch (action) {
        case "add-category":
          void addCategory();
          return;
        case "select-category": {
          const cat = actionEl.dataset["cat"];
          if (cat) selectCategory(cat);
          return;
        }
        case "trigger-upload": {
          const fileInput = root.querySelector<HTMLInputElement>("#csv-upload");
          fileInput?.click();
          return;
        }
        case "confirm-upload":
          void confirmUpload();
          return;
        default:
          return;
      }
    }

    function onChange(ev: Event): void {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      const action = target.dataset["action"];
      if (action === "file-input") {
        handleFileUpload(ev);
        return;
      }
      if (action === "update-mapping") {
        const select = target as HTMLSelectElement;
        const field = select.dataset["field"];
        if (field) updateMapping(field, select.value);
        return;
      }
    }

    root.addEventListener("click", onClick);
    root.addEventListener("change", onChange);

    // ── Kick off (mirrors source's init() → loadCategories()) ────────────

    void loadCategories();

    return (): void => {
      disposed = true;
      root.removeEventListener("click", onClick);
      root.removeEventListener("change", onChange);
    };
  },
};

// ── Mapping-field type guard ─────────────────────────────────────────────────

function isMappingField(s: string): s is MappingField {
  return (
    s === "sku" ||
    s === "description" ||
    s === "tradePrice" ||
    s === "rrp" ||
    s === "h" ||
    s === "w" ||
    s === "d" ||
    s === "doorRange" ||
    s === "colour"
  );
}

registerPage(catalogueManagerPage);

export default catalogueManagerPage;
